/**
 * Base agent abstraction for LLM streaming.
 *
 * All specialized agents (narrative, mechanics, world, animation, character)
 * extend BaseAgent and implement provider-specific streaming.
 * Integrates with the LLM router for intelligent provider selection.
 */

import type { StreamChunk, TaskType, ProviderConfig, Provider } from "../types";
import type { CallSite, ScoreDimensions } from "../router";
import { llmRouter, normalizeSpeed, normalizeCost } from "../router";
import { MODEL_REGISTRY, getModelEntry } from "../models";
import { keyCache } from "$lib/stores/keys";
import { withTimeout } from "../timeout";

/** Base interface for all LLM agents. */
export interface StreamingAgent {
  /** The task type this agent handles. */
  readonly taskType: TaskType;

  /** Stream response chunks as an async generator. */
  stream(prompt: string, context?: string): AsyncGenerator<StreamChunk, void, unknown>;

  /** Generate a complete response (non-streaming). */
  generate(prompt: string, context?: string): Promise<string>;
}

/** Default timeout for LLM calls in milliseconds (30 seconds). */
const DEFAULT_TIMEOUT_MS = 30_000;

/** Base class with common functionality for all agents. */
export abstract class BaseAgent implements StreamingAgent {
  abstract readonly taskType: TaskType;
  protected config: ProviderConfig;
  protected maxTokens: number;
  protected temperature: number;
  protected timeoutMs: number;
  /** Set by generateWithRouter() so concrete agents can pass it to dispatchStream(). */
  protected currentModelId?: string;
  /** Optional JSON Schema for provider-native structured output. */
  protected jsonSchema?: Record<string, unknown>;

  constructor(
    config: ProviderConfig,
    maxTokens = 2048,
    temperature = 0.7,
    timeoutMs = DEFAULT_TIMEOUT_MS,
  ) {
    this.config = config;
    this.maxTokens = maxTokens;
    this.temperature = temperature;
    this.timeoutMs = timeoutMs;
  }

  abstract stream(prompt: string, context?: string): AsyncGenerator<StreamChunk, void, unknown>;

  /** Default generate() buffers stream() output into a single string, with timeout. */
  async generate(prompt: string, context?: string): Promise<string> {
    const inner = async (): Promise<string> => {
      let result = "";
      for await (const chunk of this.stream(prompt, context)) {
        result += chunk.text;
      }
      return result;
    };
    return withTimeout(inner(), this.timeoutMs, `${this.taskType} agent`);
  }

  /**
   * Generate with router integration — selects best model via MAB, records metrics.
   * Builds available models from MODEL_REGISTRY + keyCache, tries up to 3 times.
   */
  async generateWithRouter(
    prompt: string,
    callSite: CallSite,
    context?: string,
    qualityScores?: Partial<ScoreDimensions>,
  ): Promise<string> {
    // Build available models from keys
    const providers: Provider[] = [
      "gemini",
      "fireworks",
      "deepseek",
      "claude",
      "together",
      "openai",
    ];
    const availableModels: { provider: Provider; modelId: string; key: string }[] = [];

    for (const provider of providers) {
      const key = await keyCache.get(provider);
      if (!key) continue;
      for (const model of MODEL_REGISTRY.filter((m) => m.provider === provider)) {
        availableModels.push({ provider, modelId: model.id, key });
      }
    }

    if (availableModels.length === 0) {
      throw new Error("No API keys configured. Add a key in Settings.");
    }

    // Try up to 3 times with different models selected by router
    const maxAttempts = 3;
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const selected = llmRouter.sampleArm(callSite, availableModels);
      const originalConfig = this.config;
      this.config = { provider: selected.provider, apiKey: selected.key };
      this.currentModelId = selected.modelId;
      const start = Date.now();

      try {
        const result = await this.generate(prompt, context);
        const latencyMs = Date.now() - start;
        const tokenCount = Math.ceil(result.length / 4); // rough estimate

        const entry = getModelEntry(selected.provider, selected.modelId);
        llmRouter.recordObservation({
          provider: selected.provider,
          modelId: selected.modelId,
          callSite,
          scores: {
            accuracy: qualityScores?.accuracy ?? 0.7,
            completeness: qualityScores?.completeness ?? 0.7,
            creativity: qualityScores?.creativity ?? 0.7,
            speed: normalizeSpeed(latencyMs),
            cost: entry
              ? normalizeCost(tokenCount, entry.costPer1kInput, entry.costPer1kOutput)
              : 0.5,
          },
          latencyMs,
          tokenCount,
          timestamp: Date.now(),
        });

        this.config = originalConfig;
        this.currentModelId = undefined;
        return result;
      } catch (e) {
        this.config = originalConfig;
        this.currentModelId = undefined;
        lastError = e instanceof Error ? e : new Error(String(e));

        // Record failure observation
        llmRouter.recordObservation({
          provider: selected.provider,
          modelId: selected.modelId,
          callSite,
          scores: { accuracy: 0, completeness: 0, creativity: 0, speed: 0, cost: 0 },
          latencyMs: Date.now() - start,
          tokenCount: 0,
          timestamp: Date.now(),
        });
      }
    }

    throw lastError ?? new Error("No providers available");
  }

  /** Build the full prompt with optional context prepended. */
  protected buildPrompt(prompt: string, context?: string): string {
    if (context) {
      return `${context}\n\n${prompt}`;
    }
    return prompt;
  }
}
